import {
  Facebook,
  Globe2,
  Instagram,
  Linkedin,
  MessageCircle,
  Music2,
  Newspaper,
  Youtube,
  type LucideProps
} from "lucide-react";
import type { ComponentType } from "react";
import type { Mention } from "@/shared/types/mention";

export type MentionPlatform = {
  label: string;
  tone: string;
  Icon: ComponentType<LucideProps>;
};

const XSocialIcon = ({ size = 24, ...props }: LucideProps) => (
  <svg
    {...props}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/>
  </svg>
);

/**
 * O que esta função de fato lê. Estrutural em vez de `Mention` inteira
 * porque a visão de publicações passa uma `Publication`, que não tem
 * classificação nenhuma — e nunca precisou ter pra isso: identificar a
 * plataforma é olhar origem, links e eixo do post.
 */
export type PlatformSource = Pick<
  Mention,
  "source" | "originalLink" | "link" | "mediaType"
>;

export function mentionPlatform(mention: PlatformSource): MentionPlatform {
  const source = mention.source.trim().toLowerCase();
  // `originalLink` primeiro: é o link do VEÍCULO (instagram.com/…), que
  // identifica a plataforma. O `link` é o da clipadora, cujo domínio é
  // sempre o mesmo e não diz nada sobre onde o post saiu.
  const url = mention.originalLink ?? mention.link ?? "";
  const haystack = `${source} ${mention.mediaType} ${url}`.toLowerCase();
  let hostname = "";
  try { hostname = new URL(url).hostname.replace(/^www\./, ""); } catch { /* URL externa inválida ou ausente */ }
  if (source.includes("instagram") || hostname.endsWith("instagram.com")) return { label: "Instagram", tone: "instagram", Icon: Instagram };
  if (source.includes("facebook") || hostname.endsWith("facebook.com") || hostname === "fb.com") return { label: "Facebook", tone: "facebook", Icon: Facebook };
  if (source.includes("linkedin") || hostname.endsWith("linkedin.com")) return { label: "LinkedIn", tone: "linkedin", Icon: Linkedin };
  if (source.includes("youtube") || hostname.endsWith("youtube.com") || hostname === "youtu.be") return { label: "YouTube", tone: "youtube", Icon: Youtube };
  if (source.includes("tiktok") || hostname.endsWith("tiktok.com")) return { label: "TikTok", tone: "tiktok", Icon: Music2 };
  if (/^x(?:\s|\/|·|-|@|$)/.test(source) || source.includes("twitter") || hostname === "x.com" || hostname.endsWith(".x.com") || hostname.endsWith("twitter.com") || hostname === "t.co") {
    return { label: "X", tone: "x", Icon: XSocialIcon };
  }
  if (source.includes("whatsapp") || hostname.endsWith("whatsapp.com") || hostname === "wa.me") return { label: "WhatsApp", tone: "whatsapp", Icon: MessageCircle };
  if (haystack.includes("noticia") || haystack.includes("notícia") || haystack.includes("imprensa")) return { label: "Portal", tone: "web", Icon: Newspaper };
  // `network` é o vocabulário do contrato real (`RawPost.post_source`).
  return { label: mention.mediaType === "network" ? "Rede social" : "Web", tone: "web", Icon: Globe2 };
}
